import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { MatMenuTrigger } from '@angular/material/menu'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import {
  ScopeElementsConfig,
  UpdateDeliverablePayload,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '@app/shared/components/scope-elements/models'
import { SectionWithChanges } from '@app/shared/components/scope-elements/models/entities-with-changes/section-with-changes.interface'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { ScopeElementsModule } from '@app/shared/components/scope-elements/scope-elements.module'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import {
  getCrFieldToDisplay,
  getCrNetChanges,
  showCrDifferences,
  showCrNetChanges,
} from '@app/shared/components/scope-elements/utils/change-request'
import { formatCrPrice, formatPercentage } from '@app/shared/components/scope-elements/utils/format-value.utils'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { formatHoursPipeFn } from '@app/shared/pipe/formatHours.pipe'
import { SharedModule } from '@app/shared/shared.module'
import { CdnConfig } from '@core/model/cdn-config.model'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { Store } from '@ngrx/store'
import { ScopeUiDatepickerComponent } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { trackById } from '@shared/utils/utils'
import { plainToInstance } from 'class-transformer'
import { floor } from 'lodash'

@Component({
  selector: 'scope-section-row',
  imports: [CommonModule, SharedModule, ScopeElementsModule, ScopeUiInputComponent, ScopeUiDatepickerComponent],
  templateUrl: './section-row.component.html',
  styleUrls: ['./section-row.component.scss'],
  standalone: true,
})
export class SectionRowComponent implements OnInit {
  @Input() tableConfig?: ScopeElementsConfig
  @Input() section!: SectionWithChanges
  @Input() feeMenuOptions!: MenuOptions[]
  @Input() deliverableMenuOptions?: MenuOptions[]
  @Input() componentMenuOptions?: MenuOptions[]
  @Input() currentRatecard: RatecardVersion
  @Input() sectionSelectedStates: Record<number, boolean>
  @Input() cdnConfig?: CdnConfig

  @Output() onFetchDeliverable = new EventEmitter<number>()
  @Output() onUpdateDeliverable = new EventEmitter<UpdateDeliverablePayload>()
  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDropDeliverable = new EventEmitter<{ event: CdkDragDrop<any>; section: ScopeSection | null }>()

  @Output() onAddComponentFee = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onOverrideComponentQuantity = new EventEmitter<ScopeComponent>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onDeleteSection = new EventEmitter<ScopeSection>()
  @Output() onAddSectionFee = new EventEmitter<ScopeSection>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateDeliverableTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateDeliverableSectionTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderDeliverableTpcs = new EventEmitter<any>()
  @Output() onReorderComponentTpcs = new EventEmitter<any>()
  @Output() onReorderDeliverableSectionTpcs = new EventEmitter<any>()
  @Output() onMoveComponent = new EventEmitter<any>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onScrolledDown = new EventEmitter()

  @ViewChild(MatMenuTrigger) startDateMenuTrigger: MatMenuTrigger
  @ViewChild(MatMenuTrigger) endDateMenuTrigger: MatMenuTrigger

  editSectionName: number

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  constructor(
    private store: Store,
    private authService: AuthService,
    protected scopeElementsService: ScopeElementsService,
    protected exchangeRateService: ExchangeRateService,
    protected scopeApprovalFlowService: ApprovalFlowService,
    protected scopeTabService: ScopeTabService,
    protected convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  ngOnInit() {
    const isSectionExpanded = this.scopeTabService.sectionSelectedStates?.[this.section?.id]
    if (isSectionExpanded) this.fetchDeliverables()
  }

  fetchDeliverables() {
    this.store.dispatch(ScopePaginatedOverviewActions.getScopeSectionDeliverables({ sectionId: this.section.id }))
  }

  toggleSection() {
    this.scopeTabService.toggleSection(this.section.id)
    this.fetchDeliverables()
  }

  updateSectionFee(feeItem: FeeItemInstance) {
    this.onUpdateFeeItem.emit({ feeItem, section: this.section })
  }

  updateSectionDescription(description: string, section: ScopeSection) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, description }),
      })
    )
  }

  updateSectionNote(internalNote: string, section: ScopeSection) {
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, internalNote }),
      })
    )
  }

  updateSectionName(name: any, section: ScopeSection) {
    this.editSectionName = null
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...section, name }),
      })
    )
  }

  updateStartDate(startDate: Date) {
    this.startDateMenuTrigger.closeMenu()
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...this.section, startDate }),
      })
    )
  }

  updateEndDate(endDate: Date) {
    this.endDateMenuTrigger.closeMenu()
    this.store.dispatch(
      ScopeOverviewActions.updateScopeSection({
        scopeId: this.tableConfig?.scopeVersion.identity.id,
        section: plainToInstance(ScopeSection, { ...this.section, endDate }),
      })
    )
  }

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<SectionWithChanges>(fieldName, this.section)
  showDiff = (fieldName: string): boolean =>
    showCrDifferences<SectionWithChanges>(this.tableConfig, this.section, fieldName)

  showNetChanges = (fieldName: string) => showCrNetChanges(this.tableConfig, false, this.section.changes?.[fieldName])
  getNetChanges = (fieldName: string, defaultValue: number) =>
    getCrNetChanges(this.section.changes, fieldName, defaultValue)

  formatPrice = (value: number | Money): string =>
    formatCrPrice(value, this.tableConfig?.currency, this.convertCurrencyPipe)
  protected readonly formatHours = formatHoursPipeFn
  protected readonly formatPercentage = formatPercentage

  protected readonly floor = floor
  protected readonly trackById = trackById
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly isPreferenceActive = isPreferenceActive
}
